import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { getConfig } from '../config';
import { ApiError, ApiErrorPayload, ManagedEnvironment } from './types';

/** Function type for getting the current access token */
export type GetAccessTokenFn = () => Promise<string>;

/** Function type for getting the current managed environment */
export type GetManagedEnvironmentFn = () => ManagedEnvironment;

/** Function type for triggering re-login on unrecoverable 401 */
export type OnUnauthenticatedFn = () => void;

export interface ApiClientOptions {
  /** Async function that returns a valid access token */
  getAccessToken: GetAccessTokenFn;
  /** Function that returns the currently selected managed environment */
  getManagedEnvironment: GetManagedEnvironmentFn;
  /** Callback invoked when silent token renewal fails (401) */
  onUnauthenticated: OnUnauthenticatedFn;
}

/**
 * Creates an axios-based HTTP client pre-configured for the Irondome BFF.
 *
 * Features:
 * - Injects `Authorization: Bearer {token}` header on every request
 * - Injects `X-Managed-Environment` header on every request
 * - Handles 401: attempts silent renewal, then triggers re-login
 * - Handles 403: throws structured ApiError (access denied)
 * - Handles 502: throws structured ApiError (backend unavailable, no inferred values)
 */
export function createApiClient(options: ApiClientOptions): AxiosInstance {
  const { getAccessToken, getManagedEnvironment, onUnauthenticated } = options;

  const config = getConfig();
  const client = axios.create({
    baseURL: config.apiBaseUrl,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // --- Request interceptor: inject auth and environment headers ---
  client.interceptors.request.use(
    async (requestConfig: InternalAxiosRequestConfig) => {
      const token = await getAccessToken();
      requestConfig.headers.set('Authorization', `Bearer ${token}`);
      requestConfig.headers.set(
        'X-Managed-Environment',
        getManagedEnvironment(),
      );
      return requestConfig;
    },
    (error) => Promise.reject(error),
  );

  // --- Response interceptor: handle error statuses ---
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiErrorPayload>) => {
      const status = error.response?.status;

      if (status === 401) {
        // Token expired — attempt silent renewal by getting a fresh token.
        // If getAccessToken throws (silent renewal failed), trigger re-login.
        try {
          const freshToken = await getAccessToken();
          // Retry original request with the fresh token
          const originalRequest = error.config;
          if (originalRequest) {
            originalRequest.headers.set(
              'Authorization',
              `Bearer ${freshToken}`,
            );
            return client.request(originalRequest);
          }
        } catch {
          // Silent renewal failed → trigger full re-login
          onUnauthenticated();
        }
        throw new ApiError(
          401,
          'UNAUTHORIZED',
          'Session expired. Redirecting to login.',
        );
      }

      if (status === 403) {
        const payload = error.response?.data;
        throw new ApiError(
          403,
          payload?.error?.code ?? 'FORBIDDEN',
          payload?.error?.message ?? 'Access denied.',
          payload?.error?.field,
        );
      }

      if (status === 502) {
        const payload = error.response?.data;
        throw new ApiError(
          502,
          payload?.error?.code ?? 'BACKEND_ERROR',
          payload?.error?.message ??
            'Backend unavailable. Please try again later.',
          payload?.error?.field,
        );
      }

      // Other errors — wrap if we have response data
      if (error.response?.data?.error) {
        const payload = error.response.data;
        throw new ApiError(
          status ?? 500,
          payload.error.code,
          payload.error.message,
          payload.error.field,
        );
      }

      return Promise.reject(error);
    },
  );

  return client;
}
